import { Link } from "react-router-dom";
import { Layers, ExternalLink, Loader2 } from "lucide-react";
import type { RunProgress } from "@mycompbox/shared";
import type { EpicProgress } from "@/lib/api/mailbox";
import { useRunProgress } from "@/hooks/useRunProgress";
import {
  currentRunStage,
  formatEta,
  isUnsuccessfulRunState,
  runCountLabel,
  runPercent,
  runStateLabel,
} from "@/lib/run-progress-format";

interface ExecutionProgressPanelProps {
  /** The job's linked epic. Only its id and name are used; all numbers come from RunProgress. */
  progress: Pick<EpicProgress, "epicId" | "name">;
}

/**
 * Live execution-progress view for a mailbox job linked to a running epic
 * (ENG-123-3, ENG-370). Percent, "N of M features", the current feature and
 * the ETA all come from the build's RunProgress (useRunProgress), the same
 * source as every other build surface, so the job page and epic page never
 * disagree. Updates arrive over SSE; the hook only polls while SSE is down.
 */
export function ExecutionProgressPanel({ progress: epic }: ExecutionProgressPanelProps) {
  const { progress, isLoading, error } = useRunProgress("build", epic.epicId);

  return (
    <div
      className="border-t border-white/[0.06] px-4 md:px-6 py-4 space-y-3"
      data-testid="execution-progress-panel"
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">Execution Progress</span>
        <Link
          to={`/epics/${epic.epicId}`}
          className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
        >
          {epic.name}
          <ExternalLink className="h-3 w-3" />
        </Link>
      </div>

      {progress ? (
        <ExecutionProgressBody progress={progress} />
      ) : isLoading ? (
        <p className="text-xs text-muted-foreground">Loading progress…</p>
      ) : error ? (
        <p className="text-xs text-muted-foreground">Progress is not available right now.</p>
      ) : null}
    </div>
  );
}

function ExecutionProgressBody({ progress }: { progress: RunProgress }) {
  const stateLabel = runStateLabel(progress.state);
  const unsuccessful = isUnsuccessfulRunState(progress.state);
  // A failed or cancelled run must not look like it is still part-way through:
  // fill the bar and colour it by outcome, with an explicit label.
  const pct = unsuccessful ? 100 : runPercent(progress);
  const barColor =
    progress.state === "failed"
      ? "bg-red-500/70"
      : progress.state === "cancelled"
        ? "bg-zinc-500/70"
        : progress.state === "stalled"
          ? "bg-amber-500/70"
          : "bg-green-500/70";
  const current = currentRunStage(progress);
  const running = progress.state === "active" || progress.state === "stalled";

  return (
    <div className="space-y-2.5">
      <div>
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
          <span className="flex items-center gap-1.5">
            <Layers className="h-3.5 w-3.5" />
            <span data-testid="run-progress-count">{runCountLabel(progress)}</span>
          </span>
          <span data-testid="run-progress-state">
            {stateLabel ?? `${String(pct)}%`}
            {running && <> · {formatEta(progress.eta)}</>}
          </span>
        </div>
        <div
          className="h-1.5 w-full rounded-full bg-white/[0.04] overflow-hidden"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          aria-valuetext={stateLabel ?? `${String(pct)}%`}
        >
          <div
            className={`h-full rounded-full ${barColor} transition-[width] duration-300`}
            style={{ width: `${String(pct)}%` }}
          />
        </div>
      </div>

      {current && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground" aria-live="polite">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span data-testid="run-progress-current">
            {current.label}
            {current.detail && <> · {current.detail}</>}
          </span>
        </div>
      )}

      {unsuccessful && progress.failureReason && (
        <p className="text-xs text-red-400" data-testid="run-progress-failure">
          {progress.failureReason}
        </p>
      )}
    </div>
  );
}
